import { supabase } from '../lib/supabase';

export const accountVerificationService = {
  /**
   * Get all failed email confirmations
   */
  async getFailedConfirmations() {
    try {
      const { data, error } = await supabase?.rpc('get_failed_confirmations');

      if (error) throw error;

      return data?.map(row => ({
        userId: row?.user_id,
        email: row?.email,
        fullName: row?.full_name,
        registrationDate: row?.registration_date,
        tokenExpiresAt: row?.token_expires_at,
        failureReason: row?.failure_reason || 'Token expired',
        retryCount: row?.retry_count || 0,
        isExpired: row?.is_expired
      })) || [];
    } catch (error) {
      console.error('Error fetching failed confirmations:', error);
      throw error;
    }
  },

  /**
   * Get verification statistics
   */
  async getVerificationStatistics() {
    try {
      const { data, error } = await supabase?.rpc('get_verification_statistics');

      if (error) throw error;

      return {
        pendingVerifications: data?.pending_verifications || 0,
        failedConfirmations: data?.failed_confirmations || 0,
        manualVerificationsToday: data?.manual_verifications_today || 0,
        emailsResentToday: data?.emails_resent_today || 0,
        successRate: data?.success_rate || 0
      };
    } catch (error) {
      console.error('Error fetching verification statistics:', error);
      throw error;
    }
  },

  /**
   * Manually verify an account
   */
  async manuallyVerifyAccount(userId, justification) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase?.rpc('manually_verify_account', {
        p_user_id: userId,
        p_admin_email: user?.email,
        p_justification: justification
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Manual verification failed');
      }

      return {
        success: true,
        message: data?.message,
        email: data?.email
      };
    } catch (error) {
      console.error('Error manually verifying account:', error);
      throw error;
    }
  },

  /**
   * Resend verification email
   */
  async resendVerificationEmail(userId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        throw new Error('Not authenticated');
      }

      // Get new token from database
      const { data, error } = await supabase?.rpc('resend_verification_email', {
        p_user_id: userId,
        p_admin_email: user?.email
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to resend verification email');
      }

      // Get user profile details
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('email, full_name, trial_end_date')
        ?.eq('id', userId)
        ?.single();

      if (profileError) throw profileError;

      // Send activation email via Edge Function
      const activationUrl = `${window?.location?.origin}/activate?token=${data?.activation_token}`;

      const { data: emailData, error: emailError } = await supabase?.functions?.invoke(
        'send-activation',
        {
          body: {
            email: profile?.email,
            fullName: profile?.full_name,
            trialEndDate: profile?.trial_end_date,
            activationToken: data?.activation_token,
            activationUrl: activationUrl
          }
        }
      );

      if (emailError) {
        console.error('Email sending error:', emailError);
        throw new Error('Failed to send activation email');
      }

      return {
        success: true,
        message: 'Verification email sent successfully',
        email: profile?.email,
        emailId: emailData?.emailId
      };
    } catch (error) {
      console.error('Error resending verification email:', error);
      throw error;
    }
  },

  /**
   * Get verification audit logs
   */
  async getVerificationAuditLogs(filters = {}) {
    try {
      let query = supabase
        ?.from('email_verification_audit')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (filters?.actionType) {
        query = query?.eq('action_type', filters?.actionType);
      }

      if (filters?.email) {
        query = query?.ilike('email', `%${filters?.email}%`);
      }

      if (filters?.dateFrom) {
        query = query?.gte('created_at', filters?.dateFrom);
      }

      if (filters?.dateTo) {
        query = query?.lte('created_at', filters?.dateTo);
      }

      const limit = filters?.limit || 50;
      query = query?.limit(limit);

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        userId: row?.user_id,
        email: row?.email,
        actionType: row?.action_type,
        failureReason: row?.failure_reason,
        retryCount: row?.retry_count,
        performedBy: row?.performed_by,
        metadata: row?.metadata,
        createdAt: row?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching verification audit logs:', error);
      throw error;
    }
  },

  /**
   * Bulk verify accounts
   */
  async bulkVerifyAccounts(userIds, justification) {
    try {
      const results = [];

      for (const userId of userIds) {
        try {
          const result = await this.manuallyVerifyAccount(userId, justification);
          results?.push({ userId, success: true, ...result });
        } catch (error) {
          results?.push({ userId, success: false, error: error?.message });
        }
      }

      return results;
    } catch (error) {
      console.error('Error bulk verifying accounts:', error);
      throw error;
    }
  },

  /**
   * Bulk resend verification emails
   */
  async bulkResendEmails(userIds) {
    try {
      const results = [];

      for (const userId of userIds) {
        try {
          const result = await this.resendVerificationEmail(userId);
          results?.push({ userId, success: true, ...result });
        } catch (error) {
          results?.push({ userId, success: false, error: error?.message });
        }
      }

      return results;
    } catch (error) {
      console.error('Error bulk resending emails:', error);
      throw error;
    }
  }
};

export default accountVerificationService;
